import type {Category} from "./api/types";

export type CategorySlugs = {
  c?: string;
  sub?: string;
  subsub?: string;
  subsubsub?: string;
};

export type CategoryNavLink = {
  label: string;
  url: string;
};

export type CategoryHeroMeta = {
  parentLabel?: string;
  title: string;
  links: CategoryNavLink[];
};

export function categoryChildLinks(
  categories: Category[],
  c?: string,
  sub?: string,
  subsub?: string
): CategoryNavLink[] {
  if(!c) {
    return categories.map((cat) => ({label: cat.nume, url: cat.url}));
  }
  const root = categories.find((cat) => cat.slug === c);
  if(!root?.subcategorii?.length) return [];
  if(!sub) {
    return root.subcategorii.map((sc) => ({label: sc.nume, url: sc.url}));
  }
  const subCat = root.subcategorii.find((sc) => sc.slug === sub);
  if(!subCat?.subsubcategorii?.length) return [];
  if(!subsub) {
    return subCat.subsubcategorii.map((ssc) => ({label: ssc.nume, url: ssc.url}));
  }
  const subSub = subCat.subsubcategorii.find((ssc) => ssc.slug === subsub);
  if(!subSub?.subsubsubcategorii?.length) return [];
  return subSub.subsubsubcategorii.map((sssc) => ({label: sssc.nume, url: sssc.url}));
}

export function resolveCategoryTitle(categories: Category[], slugs: CategorySlugs): string {
  const {c, sub, subsub, subsubsub} = slugs;
  if(!c) return "";
  const cat = categories.find((row) => row.slug === c);
  if(!cat) return "";
  if(subsubsub && subsub && sub) {
    const subcat = cat.subcategorii.find((row) => row.slug === sub);
    const subsubcat = subcat?.subsubcategorii?.find((row) => row.slug === subsub);
    const leaf = subsubcat?.subsubsubcategorii?.find((row) => row.slug === subsubsub);
    if(leaf) return leaf.nume;
  }
  if(subsub && sub) {
    const subcat = cat.subcategorii.find((row) => row.slug === sub);
    const leaf = subcat?.subsubcategorii?.find((row) => row.slug === subsub);
    if(leaf) return leaf.nume;
  }
  if(sub) {
    const leaf = cat.subcategorii.find((row) => row.slug === sub);
    if(leaf) return leaf.nume;
  }
  return cat.nume;
}

export function getCategoryHeroMeta(
  categories: Category[],
  slugs: CategorySlugs,
  fallbackTitle = ""
): CategoryHeroMeta {
  const {c, sub, subsub, subsubsub} = slugs;
  const links = categoryChildLinks(categories, c, sub, subsub);
  if(!c) {
    return {title: fallbackTitle, links: []};
  }
  const cat = categories.find((row) => row.slug === c);
  if(!cat) {
    return {title: fallbackTitle || c, links};
  }
  if(subsubsub && subsub && sub) {
    const subcat = cat.subcategorii.find((row) => row.slug === sub);
    const subsubcat = subcat?.subsubcategorii?.find((row) => row.slug === subsub);
    const leaf = subsubcat?.subsubsubcategorii?.find((row) => row.slug === subsubsub);
    return {
      parentLabel: subsubcat?.nume,
      title: leaf?.nume || fallbackTitle,
      links: []
    };
  }
  if(subsub && sub) {
    const subcat = cat.subcategorii.find((row) => row.slug === sub);
    const subsubcat = subcat?.subsubcategorii?.find((row) => row.slug === subsub);
    return {
      parentLabel: subcat?.nume,
      title: subsubcat?.nume || fallbackTitle,
      links
    };
  }
  if(sub) {
    const subcat = cat.subcategorii.find((row) => row.slug === sub);
    return {
      parentLabel: cat.nume,
      title: subcat?.nume || fallbackTitle,
      links
    };
  }
  return {
    title: cat.nume || fallbackTitle,
    links
  };
}
